import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthenticationPort } from '../AuthenticationFactory';
import { AuthenticatedUser, LoginOptions, LogoutOptions } from '../models';
import { v4 as uuid4 } from 'uuid';
import { IPermission } from '../../modules/onboarding/onboardingModels';
import { authService } from '../services';

const SESSION_TIMEOUT_SECONDS = 3600;

export interface UseAuthServiceReturn {
  user: AuthenticatedUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  signIn: (options?: LoginOptions) => Promise<void>;
  signOut: (options?: LogoutOptions) => Promise<void>;
  
  authFetch: (input: RequestInfo, init?: RequestInit) => Promise<Response>;
  
  hasPermission: (permission: string) => boolean;
  hasGlobalPermission: (permission: string) => boolean;
  globalPermissions: IPermission[];
  loadGlobalPermissions: () => Promise<void>;
  fetchUserAllPrivileges: (scope: string) => Promise<IPermission[]>;
  checkLicensesPrivilege: (
    email: string | undefined,
    sub: string | undefined,
    siOrgId: string
  ) => Promise<boolean>;
  
  resetSessionTimer: () => void;
  
  baseURL: string;
  offering: string;
}

export function useAuthService(): UseAuthServiceReturn {
  const authPort = useAuthenticationPort();
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [globalPermissions, setGlobalPermissions] = useState<IPermission[]>([]);
  
  const lastActiveTimeRef = useRef<number>(Date.now());
  
  const env = (window as any).ENV;
  const baseURL = env?.BASE_URL || '';
  const offering = env?.OFFERING || '';

  useEffect(() => {
    let mounted = true;

    const loadUser = async () => {
      try {
        const currentUser = await authPort.getCurrentUser();
        if (mounted) {
          setUser(currentUser);
        }
      } catch (error) {
        console.error('[useAuthService] Error loading user:', error);
        if (mounted) {
          setUser(null);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadUser();

    const unsubscribe = authPort.onAuthStateChanged((event) => {
      if (mounted) {
        setUser(event.user ?? null);
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [authPort]);

  useEffect(() => {
    const isHonSSOLogin = localStorage.getItem('selectedLoginOption') === 'SSO';
    
    if (isHonSSOLogin || !user) {
      return;
    }

    const sessionInterval = setInterval(() => {
      const currentTime = Date.now();
      const idleSeconds = (currentTime - lastActiveTimeRef.current) / 1000;

      if (idleSeconds > SESSION_TIMEOUT_SECONDS) {
        clearInterval(sessionInterval);
        if (!window.location.href.includes('signup')) {
          sessionStorage.setItem('POST_SIGNIN_LOCATION', window.location.href);
          authPort.signIn().catch(console.error);
        }
      }
    }, 1000);

    return () => {
      clearInterval(sessionInterval);
    };
  }, [user, authPort]);

  const signIn = useCallback(async (options?: LoginOptions) => {
    try {
      await authPort.signIn(options);
    } catch (error) {
      console.error('[useAuthService] Sign in error:', error);
      throw error;
    }
  }, [authPort]);

  const signOut = useCallback(async (options?: LogoutOptions) => {
    try {
      sessionStorage.setItem('POST_SIGNIN_LOCATION', window.location.href);
      if (user) {
        sessionStorage.removeItem(`globalPermissions:${user.email}`);
        sessionStorage.removeItem('NEW_SUBSCRIPTION_NOTIFICATION');
      }
      
      await authPort.signOut(options);
      setUser(null);
      setGlobalPermissions([]);
    } catch (error) {
      console.error('[useAuthService] Sign out error:', error);
      throw error;
    }
  }, [authPort, user]);

  const authFetch = useCallback(async (
    input: RequestInfo,
    init?: RequestInit
  ): Promise<Response> => {
    try {
      const token = await authPort.getAccessToken();
      const headers = new Headers(init?.headers);
      headers.set('Authorization', `Bearer ${token.value}`);
      headers.set('X-SVCorId', uuid4());
      
      return fetch(input, {
        ...init,
        headers
      });
    } catch (error) {
      console.error('[useAuthService] Auth fetch error:', error);
      throw new Error('Authentication required');
    }
  }, [authPort]);

  const loadGlobalPermissions = useCallback(async () => {
    if (!user) {
      return;
    }

    try {
      const cachedPerms = sessionStorage.getItem(`globalPermissions:${user.email}`);
      if (cachedPerms) {
        setGlobalPermissions(JSON.parse(cachedPerms));
        return; // Return early - no need to fetch if cache exists
      }

      const response = await authFetch(
        `${baseURL}/api/v1/users/me/privileges?scopes=UserManagement&scopes=OrgManagement&scopes=SubscriptionManagement&scopes=AssetModeling&scopes=ServiceAccountManagement&scopes=LicenseManagement`
      );

      if (response.ok) {
        const permissions = await response.json();
        setGlobalPermissions(permissions);
        sessionStorage.setItem(`globalPermissions:${user.email}`, JSON.stringify(permissions));
      }
    } catch (error) {
      console.error('[useAuthService] Error loading permissions:', error);
    }
  }, [user, authFetch, baseURL]);

  const hasPermission = useCallback(
    (permission: string): boolean => globalPermissions.some((p) => p.name === permission),
    [globalPermissions]
  );

  const hasGlobalPermission = useCallback(
    (permission: string): boolean => hasPermission(permission),
    [hasPermission]
  );

  const resetSessionTimer = useCallback(() => {
    lastActiveTimeRef.current = Date.now();
  }, []);

  const fetchUserAllPrivileges = useCallback(
    async (scope: string): Promise<IPermission[]> => {
      return authService.fetchUserAllPrivileges(scope);
    },
    []
  );

  const checkLicensesPrivilege = useCallback(
    async (
      email: string | undefined,
      sub: string | undefined,
      siOrgId: string
    ): Promise<boolean> => {
      return authService.checkLicensesPrivilege(email, sub, siOrgId);
    },
    []
  );

  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    signIn,
    signOut,
    authFetch,
    hasPermission,
    hasGlobalPermission,
    globalPermissions,
    loadGlobalPermissions,
    fetchUserAllPrivileges,
    checkLicensesPrivilege,
    resetSessionTimer,
    baseURL,
    offering
  };
}


export { useAuthenticationPort } from '../AuthenticationFactory';
